import { MarkdownMessage } from "@via/components/markdown/markdown-message.tsx";
import { getDocsUrl } from "@via/utils/config.tsx";
import { useState } from "react";

interface ProductUpdatesPanelProps {
  changelogBody?: string;
  breakingChangesBody?: string;
  isAdmin: boolean;
}

// The panel carries a summarized changelog; these pages carry the full text.
const DOCS_PATH = {
  "whats-new": "/product-updates/whats-new/",
  "upgrade-notes": "/product-updates/upgrade-notes/",
} as const;

export function ProductUpdatesPanel({
  changelogBody,
  breakingChangesBody,
  isAdmin,
}: ProductUpdatesPanelProps): JSX.Element {
  const [tab, setTab] = useState<"whats-new" | "upgrade-notes">("whats-new");
  const showUpgradeNotes = isAdmin && tab === "upgrade-notes";

  return (
    <div className="max-h-[80vh] px-4 py-2">
      {isAdmin && (
        <div className="mb-2 flex gap-4 border-b border-gray-200 text-sm">
          <button
            data-testid="whatsNewTab"
            className={`pb-1 ${
              tab === "whats-new"
                ? "border-b-2 border-indigo-600 font-medium text-indigo-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setTab("whats-new")}>
            What&apos;s New
          </button>
          <button
            data-testid="upgradeNotesTab"
            className={`pb-1 ${
              tab === "upgrade-notes"
                ? "border-b-2 border-indigo-600 font-medium text-indigo-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setTab("upgrade-notes")}>
            Upgrade Notes
          </button>
        </div>
      )}
      {showUpgradeNotes ? (
        breakingChangesBody ? (
          <div className="changelog-markdown-editor">
            <MarkdownMessage
              content={breakingChangesBody}
              className="max-h-[70vh] overflow-y-auto text-sm"
            />
          </div>
        ) : (
          <p className="text-gray-500">Loading upgrade notes...</p>
        )
      ) : changelogBody ? (
        <div className="changelog-markdown-editor">
          <MarkdownMessage
            content={changelogBody}
            className="max-h-[70vh] overflow-y-auto text-sm"
          />
        </div>
      ) : (
        <p className="text-gray-500">Loading changelog...</p>
      )}
      <div className="mt-2 border-t border-gray-200 pt-2 text-sm">
        <a
          data-testid="fullReleaseNotesLink"
          href={getDocsUrl(
            DOCS_PATH[showUpgradeNotes ? "upgrade-notes" : "whats-new"]
          )}
          target="_blank"
          rel="noopener noreferrer"
          className="text-indigo-600 hover:underline">
          Full release notes
        </a>
      </div>
    </div>
  );
}
